/**
 * useExportPresentationThemes — theme choices for the Export modal's
 * "Select template" dropdown, fetched only while the modal is open. The
 * theme file's `defaultTheme` is the initial selection; a pick applies to
 * that one download only (PresentationDownloadRequest.theme).
 */
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../services/queryKeys";
import { getApiErrorMessage } from "../services/apiErrors";
import { fetchPresentationThemes } from "../services/presentationThemes/presentationThemesApi";
import { mapPresentationThemeCatalog } from "../services/presentationThemes/presentationThemesMappers";
import type { PresentationThemeOption } from "../services/presentationThemes/presentationThemes.types";

export interface UseExportPresentationThemesResult {
  options: PresentationThemeOption[];
  defaultThemeKey: string | undefined;
  isLoading: boolean;
  /** Backend's reason when the catalog can't be loaded. */
  errorMessage: string | null;
}

export function useExportPresentationThemes(enabled: boolean): UseExportPresentationThemesResult {
  const query = useQuery({
    queryKey: queryKeys.presentationThemes.list(),
    queryFn: fetchPresentationThemes,
    select: mapPresentationThemeCatalog,
    enabled,
    // Only changes when the theme file is re-published.
    staleTime: Infinity,
    retry: 1,
  });
  return {
    options: query.data?.options ?? [],
    defaultThemeKey: query.data?.defaultThemeKey,
    isLoading: query.isLoading && enabled,
    errorMessage: query.isError
      ? getApiErrorMessage(query.error, "Couldn't load presentation themes.")
      : null,
  };
}
